import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { createFlag } from "../features/flag/flagSlice";

const flagValidationSchema = yup.object().shape({
  reason: yup.string().required("Please select a reason"),
  otherReason: yup.string().when("reason", {
    is: "Other",
    then: (schema) =>
      schema
        .required("Please explain the reason")
        .min(10, "Please provide at least 10 characters"),
    otherwise: (schema) => schema.optional(),
  }),
  extraInfo: yup.string().optional(),
});

const flagReasons = [
  // "Incorrect Points",
  "Incorrect Position",
  "Incorrect District",
  "Incorrect Date",
  "Duplicate Entry",
  "Other",
];

export default function FlagDialog({
  open,
  onClose,
  event,
  playerName,
  isSubmitting,
}) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.flag.isLoading);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(flagValidationSchema),
    defaultValues: {
      reason: "",
      otherReason: "",
      extraInfo: "",
    },
  });

  const reasonValue = watch("reason");

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    const flagData = {
      eventName: event.name,
      eventDate: event.date,
      playerName,
      reason: data.reason === "Other" ? data.otherReason : data.reason,
      extraInfo: data.extraInfo,
      entryId: event.entryId || null,
    };

    await dispatch(createFlag(flagData));
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Flag Entry</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {event && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{ mb: 3, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Event: <strong>{event.name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: <strong>{event.formattedDate || event.date}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Position: <strong>{event.position}</strong>
              </Typography>
            </Box>

            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.reason}>
                  <InputLabel>Reason</InputLabel>
                  <Select {...field} label="Reason">
                    {flagReasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reason && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.reason.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            {reasonValue === "Other" && (
              <Controller
                name="otherReason"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Other Reason"
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                    placeholder="Please explain the issue..."
                    error={!!errors.otherReason}
                    helperText={errors.otherReason?.message}
                  />
                )}
              />
            )}

            <Controller
              name="extraInfo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Extra Information"
                  multiline
                  rows={3}
                  placeholder="Any additional details that would help us investigate..."
                  error={!!errors.extraInfo}
                  helperText={errors.extraInfo?.message}
                />
              )}
            />
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? "Submitting..." : "Submit Flag"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
